import { some, isNull, isEmpty, isEqual } from 'lodash';
import moment from 'moment';

import {
  ItemExtended,
  ItemCreateExtended,
  ItemsImporter,
  ItemTask,
  ItemFilter,
  ItemFilterV2Base,
  ItemPresetData,
} from '~services/item/types';
import { ApiTransactionRouteItem } from '~services/apiTransaction/types';
import {
  ItemImportStateTypes,
  PriorityStateTypes,
  ItemTypes,
  RouteItemStateType,
  ItemTaskStatusType,
  SecurityDeliveryLevel,
} from '~globals/types/enums';
import { CustomerBase } from '~services/customer/types';
import { pluralize } from '~utils/commons';
import { formatterDate } from '~utils/formatter';
import { Nullable, SelectOption } from '~globals/types/commons';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';
import {
  ORDERS_GLOBALS_OPTIONS_WITHOUT_COLLECT,
  COLLECT_STATE_OPTIONS,
} from '~constants/ordersStatesOptions';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';
import { OrdersListFilters } from '~hooks/useOrdersListFilters';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { DriverCompanyBase } from '~services/driver/types';
import { VehicleBase } from '~services/vehicle/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { RouteUserBasic } from '~services/route/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { PartnerCompanyItemFilterV2Base } from '~services/partnerCompany/types';

export const validateImportErrorsInOrders = (orders: ItemExtended[]): boolean =>
  some(
    orders,
    (order) => order.itemImportStateTypeId === ItemImportStateTypes.Error,
  );

export const getDataByCustomer = (
  customer: CustomerBase,
): ItemCreateExtended => ({
  accessCode: customer.accessCode,
  address: customer.address,
  addressNotes: customer.addressNotes,
  amount: 1,
  amountPaid: null,
  bookClient: false,
  cashOnDeliveryAmount: 0,
  code: customer.code,
  contactEmail: customer.contactEmail,
  contactIdentification: customer.contactIdentification,
  contactMobile: customer.contactMobile,
  contactName: customer.contactName,
  contactPhone: customer.contactPhone,
  customField1: customer.customField1,
  customField2: customer.customField2,
  customField3: customer.customField3,
  deliveryNotes: null,
  invoice: '',
  itemId: DEFAULT_UUIID_EMPTY,
  itemStateTypeId: RouteItemStateType.New,
  itemTypeId: ItemTypes.Discharge,
  latitude: customer.latitude,
  longitude: customer.longitude,
  maxDeliveryDateTime: null,
  order: null,
  priorityTypeId: PriorityStateTypes.Normal,
  ratio: null,
  referenceId: '',
  requestPaymentOnDestiny: false,
  sellerMail: '',
  serviceDuration: customer.serviceDuration ?? 600,
  shipment: '',
  tags: '',
  timeWindowAllDay: customer.timeWindowAllDay,
  timeWindowFrom1: customer.timeWindowFrom1,
  timeWindowFrom2: customer.timeWindowFrom2,
  timeWindowTo1: customer.timeWindowTo1,
  timeWindowTo2: customer.timeWindowTo2,
  title: customer.legalName,
  trackingCode: DEFAULT_UUIID_EMPTY,
  unit1: 1,
  unit2: 0,
  validateContactIdentification: customer.validateContactIdentification,
  skillsNeeded: '',
  itemTasks: [],
  itemCollects: [],
  securityDeliveryLevel:
    customer.securityDeliveryLevel ?? SecurityDeliveryLevel.Low,
  numberOfPackages: 1,
  requestTicketValidation: false,
  routeItemStateTypeId: RouteItemStateType.New,
  store: customer.store,
  zipCode: customer.zipCode,
  collectCompanyId: null,
  qrcode: null,
  serviceType: customer.serviceType,
  zoneId: null,
  price: 0,
  isCollectItem: false,
});

const getTimeNumber = (dateStr: Nullable<string>): number =>
  dateStr ? new Date(dateStr).getTime() : Number.MAX_SAFE_INTEGER;

export const sortOrderPendingByExpirationDate = (
  orders: ItemExtended[],
): ItemExtended[] =>
  orders.sort(
    (a, b) =>
      getTimeNumber(a.maxDeliveryDateTime) -
      getTimeNumber(b.maxDeliveryDateTime),
  );

export const getImporterOrderFromCSVAlertDescription = ({
  importedRows,
  totalRows,
  errorRows,
}: ItemsImporter): string | string[] => {
  let text: string | string[] = `Se ${pluralize({
    singular: 'importó',
    plural: 'importaron',
    count: importedRows,
  })} ${pluralize({
    singular: 'pedido',
    count: importedRows,
    includeCount: true,
  })} correctamente`;

  if (errorRows >= 1) {
    const sendOrdersText = `Se ${pluralize({
      singular: 'envió',
      plural: 'enviaron',
      count: totalRows,
    })} ${pluralize({
      singular: 'pedido',
      count: totalRows,
      includeCount: true,
    })}`;

    const successOrdersImported = `${importedRows} se ${pluralize({
      singular: 'importó',
      plural: 'importaron',
      count: importedRows,
    })} correctamente`;

    const errorsOrdersImported = `${errorRows} se ${pluralize({
      singular: 'importó',
      plural: 'importaron',
      count: errorRows,
    })} con errores`;

    text = [sendOrdersText, successOrdersImported, errorsOrdersImported];
  }

  return text;
};

export const convertItemCreateExtendedToItemExtended = (
  item: ItemCreateExtended,
): ItemExtended => ({
  address: item.address,
  amount: item.amount,
  apiTransactionId: null,
  canDelete: item.canDelete ?? true,
  canModify: item.canModify ?? true,
  carrierCompanyId: null,
  carrierCompanyName: null,
  collectItemStateTypeId: null,
  companyId: '',
  contactEmail: item.contactEmail,
  contactIdentification: item.contactIdentification,
  contactMobile: item.contactMobile,
  contactName: item.contactName,
  contactPhone: item.contactPhone,
  creationDateTime: '',
  customField1: item.customField1,
  customField2: item.customField2,
  customField3: item.customField3,
  driver: null,
  duplicated: false,
  estimatedArrivalDateTime: null,
  giverCompanyId: null,
  giverCompanyName: null,
  hasFeedback: false,
  isCollected: false,
  isCollectItem: false,
  itemId: item.itemId,
  itemImportStateType: {
    itemImportStateTypeId: 2,
    description: 'Satisfactorio',
  },
  itemImportStateTypeId: 2,
  itemStateTypeId: item.itemStateTypeId,
  itemTypeId: item.itemTypeId,
  lastItemStory: null,
  lastItemStoryId: null,
  lastItemStoryType: null,
  lastItemStoryTypeId: null,
  maxDeliveryDateTime: item.maxDeliveryDateTime,
  numberOfPackages: item.numberOfPackages,
  originAddress: null,
  partnerItemCarrierStatusType: null,
  partnerItemCarrierStatusTypeId: null,
  partnerItemGiverStatusType: null,
  partnerItemGiverStatusTypeId: null,
  qrcode: item.qrcode,
  quantityArticles: null,
  quantityCompletedArticles: null,
  quantityWithErrorsArticles: null,
  readOnly: false,
  realArrivalDateTime: null,
  referenceId: item.referenceId,
  routeCode: '',
  routeId: null,
  routeItemId: null,
  routeItemStateTypeId: item.routeItemStateTypeId,
  securityDeliveryLevel: item.securityDeliveryLevel,
  serviceType: item.serviceType,
  skillsNeeded: item.skillsNeeded,
  sourceImportTypeId: null,
  tags: item.tags,
  timeWindowAllDay: item.timeWindowAllDay,
  timeWindowFrom1: item.timeWindowFrom1,
  timeWindowFrom2: item.timeWindowFrom2,
  timeWindowTo1: item.timeWindowTo1,
  timeWindowTo2: item.timeWindowTo2,
  title: item.title,
  trackingCode: item.trackingCode,
  unit1: item.unit1,
  unit2: item.unit2,
  vehicle: null,
  zipCode: item.zipCode,
  zone: null,
  zoneId: item.zoneId,
  externalStatusType: null,
  externalSubstatusType: null,
  price: item.price ?? 0,
  billingStatusTypeId: null,
  shipPrice: null,
  assignedUser: null,
});

export const convertApiTransactionRouteItemInItemExtended = (
  routeItem: ApiTransactionRouteItem,
  apiTransactionId: Nullable<string>,
): ItemExtended => ({
  address: routeItem.item?.address ?? '',
  apiTransactionId,
  contactMobile: routeItem.item?.contactMobile ?? '',
  contactPhone: routeItem.item?.contactPhone ?? '',
  contactName: routeItem.item?.contactName ?? '',
  contactIdentification: routeItem.item?.contactIdentification ?? '',
  contactEmail: routeItem.item?.contactEmail ?? '',
  customField1: routeItem.item?.customField1 ?? '',
  customField2: routeItem.item?.customField2 ?? '',
  customField3: routeItem.item?.customField3 ?? '',
  creationDateTime: routeItem.item?.creationDateTime ?? '',
  estimatedArrivalDateTime: routeItem.estimatedArrivalDateTime,
  hasFeedback: routeItem.hasFeedback,
  itemId: routeItem.itemId,
  itemImportStateType: {
    itemImportStateTypeId: ItemImportStateTypes.Successfully,
    description: 'Satisfactorio',
  },
  itemImportStateTypeId: ItemImportStateTypes.Successfully,
  itemTypeId: routeItem.item?.itemTypeId ?? ItemTypes.Charge,
  maxDeliveryDateTime: routeItem.item?.maxDeliveryDateTime ?? null,
  realArrivalDateTime: routeItem.realArrivalDateTime,
  referenceId: routeItem.item?.referenceId ?? '',
  routeCode: '',
  routeId: routeItem.routeId,
  routeItemId: routeItem.routeItemId,
  routeItemStateTypeId: routeItem.routeItemStateTypeId,
  itemStateTypeId: routeItem.routeItemStateTypeId,
  skillsNeeded: routeItem.item?.skillsNeeded ?? null,
  tags: routeItem.item?.tags ?? '',
  timeWindowAllDay: routeItem.item?.timeWindowAllDay ?? true,
  timeWindowFrom1: routeItem.item?.timeWindowFrom1 ?? null,
  timeWindowFrom2: routeItem.item?.timeWindowFrom2 ?? null,
  timeWindowTo1: routeItem.item?.timeWindowTo1 ?? null,
  timeWindowTo2: routeItem.item?.timeWindowTo2 ?? null,
  title: routeItem.item?.title ?? '',
  trackingCode: routeItem.item?.trackingCode ?? '',
  unit1: routeItem.item?.unit1 ?? 1,
  unit2: routeItem.item?.unit2 ?? 0,
  amount: routeItem.item?.amount ?? 0,
  carrierCompanyName: null,
  partnerItemCarrierStatusType: null,
  partnerItemCarrierStatusTypeId: null,
  partnerItemGiverStatusType: null,
  partnerItemGiverStatusTypeId: null,
  quantityArticles: null,
  quantityCompletedArticles: null,
  quantityWithErrorsArticles: null,
  securityDeliveryLevel: null,
  giverCompanyName: null,
  isCollected: false,
  isCollectItem: false,
  originAddress: null,
  carrierCompanyId: null,
  collectItemStateTypeId: null,
  giverCompanyId: null,
  readOnly: false,
  companyId: (routeItem.item?.company.id ?? routeItem.warehouse?.id) as string,
  numberOfPackages: routeItem.item?.numberOfPackages ?? 1,
  zipCode: routeItem.item?.zipCode ?? '',
  canDelete: true,
  canModify: true,
  duplicated: false,
  sourceImportTypeId: null,
  qrcode: null,
  lastItemStory: null,
  lastItemStoryId: null,
  lastItemStoryType: null,
  lastItemStoryTypeId: null,
  driver: null,
  vehicle: null,
  serviceType: null,
  zoneId: null,
  zone: null,
  externalStatusType: null,
  externalSubstatusType: null,
  price: routeItem.item?.price ?? 0,
  billingStatusTypeId: null,
  shipPrice: null,
  assignedUser: null,
});

export const isDelayedOrder = (
  estimatedArrivalDateTime: Nullable<string>,
): boolean => {
  if (estimatedArrivalDateTime) {
    return moment(estimatedArrivalDateTime).isBefore(moment(), 'minutes');
  }

  return false;
};

export const parseItemTasksToArray = (itemTasks?: ItemTask[]): string[] =>
  itemTasks ? itemTasks.map((itemTask) => itemTask.description) : [];

export const parseItemTasksToListArray = (itemTasks: string[]): ItemTask[] =>
  itemTasks.map((itemTask) => ({
    description: itemTask,
    itemId: null,
    itemTaskId: null,
    itemTaskStatusType: null,
    itemTaskStatusTypeId: ItemTaskStatusType.Pending,
    notes: null,
  }));

export const isPendingOrder = (order: ItemExtended): boolean => {
  const hasNewState =
    isNull(order.itemStateTypeId) ||
    isEqual(order.itemStateTypeId, RouteItemStateType.New);

  return (
    hasNewState && isEmpty(order.routeCode) && isNull(order.apiTransactionId)
  );
};

export const isOnAgendaOrder = (order: ItemExtended): boolean =>
  [RouteItemStateType.OnAgenda, RouteItemStateType.InProgress].includes(
    order.itemStateTypeId as RouteItemStateType,
  );

export const validateServiceType = (
  currentServiceType: string,
  validServicesTypes: string[],
): boolean => validServicesTypes.includes(currentServiceType);

export const getValidServiceType = (
  currentServiceType: Nullable<string>,
  validServicesTypes: string[],
): Nullable<string> => {
  if (
    !currentServiceType ||
    !validateServiceType(currentServiceType, validServicesTypes)
  )
    return null;

  return currentServiceType;
};

export const validateCustomerType = (
  currentCustomerType: string,
  validCustomerTypes: string[],
): boolean => validCustomerTypes.includes(currentCustomerType);

export const getValidCustomerType = (
  currentCustomerType: Nullable<string>,
  validCustomerTypes: string[],
): Nullable<string> => {
  if (
    !currentCustomerType ||
    !validateServiceType(currentCustomerType, validCustomerTypes)
  )
    return null;

  return currentCustomerType;
};

export const convertPresetFilterToOrderListFilters = (
  presetFilters: ItemPresetData,
  selectedData: {
    drivers: DriverCompanyBase[];
    vehicles: VehicleBase[];
    services: SelectOption[];
    logisticsZones: LogisticZoneBase[];
    users: RouteUserBasic[];
  },
  accessFeature: {
    limitNoPro: boolean;
    mgmZones: boolean;
  },
): OrdersListFilters => {
  const dateFrom = presetFilters.dateFrom
    ? new Date(presetFilters.dateFrom)
    : null;

  const dateTo = presetFilters.dateTo ? new Date(presetFilters.dateTo) : null;

  const drivers =
    presetFilters.driversId && presetFilters.driversId.length > 0
      ? selectedData.drivers.filter((driver) =>
          presetFilters.driversId?.includes(driver.id),
        )
      : null;

  const vehicles =
    presetFilters.vehiclesId && presetFilters.vehiclesId.length > 0
      ? selectedData.vehicles.filter((vehicle) =>
          presetFilters.vehiclesId?.includes(vehicle.id),
        )
      : null;

  const search = presetFilters.routeCode ?? '';

  const expirationDate = presetFilters.maxDeliveredDateTime
    ? new Date(presetFilters.maxDeliveredDateTime)
    : null;

  let state =
    presetFilters.routeItemStateTypesId !== null &&
    presetFilters.routeItemStateTypesId !== undefined &&
    presetFilters.routeItemStateTypesId.length > 0
      ? presetFilters.routeItemStateTypesId[0]
      : null;

  const itemState =
    presetFilters.collectItemStateTypesId !== null &&
    presetFilters.collectItemStateTypesId !== undefined &&
    presetFilters.collectItemStateTypesId?.length > 0
      ? presetFilters.collectItemStateTypesId[0]
      : null;

  if (itemState) {
    state = RouteItemStateType.New;
  }

  let services =
    presetFilters.serviceTypeList && presetFilters.serviceTypeList.length > 0
      ? selectedData.services.filter((service) =>
          presetFilters.serviceTypeList?.includes(service.value.toString()),
        )
      : null;

  let zones =
    presetFilters.zonesId && presetFilters.zonesId.length > 0
      ? selectedData.logisticsZones.filter((zone) =>
          presetFilters.zonesId?.includes(zone.zoneId),
        )
      : null;

  let unit1Gt =
    presetFilters.unit1GraterThan !== null &&
    presetFilters.unit1GraterThan !== undefined
      ? String(presetFilters.unit1GraterThan)
      : '';

  let unit1Lt =
    presetFilters.unit1LowerThan !== null &&
    presetFilters.unit1LowerThan !== undefined
      ? String(presetFilters.unit1LowerThan)
      : '';

  let unit2Gt =
    presetFilters.unit2GraterThan !== null &&
    presetFilters.unit2GraterThan !== undefined
      ? String(presetFilters.unit2GraterThan)
      : '';

  let unit2Lt =
    presetFilters.unit2LowerThan !== null &&
    presetFilters.unit2LowerThan !== undefined
      ? String(presetFilters.unit2LowerThan)
      : '';

  const priceGt =
    presetFilters.priceGraterThan !== null &&
    presetFilters.priceGraterThan !== undefined
      ? String(presetFilters.priceGraterThan)
      : '';

  const priceLt =
    presetFilters.priceLowerThan !== null &&
    presetFilters.priceLowerThan !== undefined
      ? String(presetFilters.priceLowerThan)
      : '';

  let assignedUser =
    presetFilters.assignedUserId !== null &&
    presetFilters.assignedUserId !== undefined
      ? selectedData.users.filter(
          (user) => user.userId === presetFilters.assignedUserId,
        )[0]
      : null;

  if (!accessFeature.limitNoPro) {
    services = null;
    unit1Gt = '';
    unit1Lt = '';
    unit2Gt = '';
    unit2Lt = '';
    assignedUser = null;
  }

  if (!accessFeature.mgmZones) {
    zones = null;
  }

  return {
    dateFrom,
    dateTo,
    drivers,
    vehicles,
    search,
    expirationDate,
    state,
    itemState,
    services,
    zones,
    unit1Gt,
    unit1Lt,
    unit2Gt,
    unit2Lt,
    priceGt,
    priceLt,
    assignedUser,
  };
};

export const convertPresetFilterToItemSearchFilters = (
  presetFilters: ItemPresetData,
  selectedData: {
    drivers: DriverCompanyBase[];
    vehicles: VehicleBase[];
    services: SelectOption[];
    logisticsZones: LogisticZoneBase[];
    assignedUsers: RouteUserBasic[];
    giverCompanies: AuthExtendedCompany[];
  },
  accessFeature: {
    limitNoPro: boolean;
    mgmZones: boolean;
  },
): ItemsSearchFilters => {
  const search = presetFilters.routeCode ?? '';

  const expirationDateTimeFrom = presetFilters.maxDeliveredDateTimeFrom
    ? new Date(presetFilters.maxDeliveredDateTimeFrom)
    : null;

  const expirationDateTimeTo = presetFilters.maxDeliveredDateTimeTo
    ? new Date(presetFilters.maxDeliveredDateTimeTo)
    : null;

  const includeWithoutExpirationDate =
    presetFilters.includeWithoutMaxDeliveryDatetime !== null &&
    presetFilters.includeWithoutMaxDeliveryDatetime !== undefined
      ? Boolean(presetFilters.includeWithoutMaxDeliveryDatetime)
      : null;

  const dateFrom = presetFilters.dateFrom
    ? new Date(presetFilters.dateFrom)
    : null;

  const dateTo = presetFilters.dateTo ? new Date(presetFilters.dateTo) : null;

  const finalizedDateTimeFrom = presetFilters.finalizedDateTimeFrom
    ? new Date(presetFilters.finalizedDateTimeFrom)
    : null;

  const finalizedDateTimeTo = presetFilters.finalizedDateTimeTo
    ? new Date(presetFilters.finalizedDateTimeTo)
    : null;

  let unit1Gt =
    presetFilters.unit1GraterThan !== null &&
    presetFilters.unit1GraterThan !== undefined
      ? String(presetFilters.unit1GraterThan)
      : '';

  let unit1Lt =
    presetFilters.unit1LowerThan !== null &&
    presetFilters.unit1LowerThan !== undefined
      ? String(presetFilters.unit1LowerThan)
      : '';

  let unit2Gt =
    presetFilters.unit2GraterThan !== null &&
    presetFilters.unit2GraterThan !== undefined
      ? String(presetFilters.unit2GraterThan)
      : '';

  let unit2Lt =
    presetFilters.unit2LowerThan !== null &&
    presetFilters.unit2LowerThan !== undefined
      ? String(presetFilters.unit2LowerThan)
      : '';

  const priceGt =
    presetFilters.priceGraterThan !== null &&
    presetFilters.priceGraterThan !== undefined
      ? String(presetFilters.priceGraterThan)
      : '';

  const priceLt =
    presetFilters.priceLowerThan !== null &&
    presetFilters.priceLowerThan !== undefined
      ? String(presetFilters.priceLowerThan)
      : '';

  const vehicles =
    presetFilters.vehiclesId && presetFilters.vehiclesId.length > 0
      ? selectedData.vehicles.filter((vehicle) =>
          presetFilters.vehiclesId?.includes(vehicle.id),
        )
      : null;

  const drivers =
    presetFilters.driversId && presetFilters.driversId.length > 0
      ? selectedData.drivers.filter((driver) =>
          presetFilters.driversId?.includes(driver.id),
        )
      : null;

  let services =
    presetFilters.serviceTypeList && presetFilters.serviceTypeList.length > 0
      ? selectedData.services.filter((service) =>
          presetFilters.serviceTypeList?.includes(service.value.toString()),
        )
      : null;

  let zones =
    presetFilters.zonesId && presetFilters.zonesId.length > 0
      ? selectedData.logisticsZones.filter((zone) =>
          presetFilters.zonesId?.includes(zone.zoneId),
        )
      : null;

  let assignedUser =
    presetFilters.assignedUserId !== null &&
    presetFilters.assignedUserId !== undefined
      ? selectedData.assignedUsers.filter(
          (currentAssignedUser) =>
            currentAssignedUser.userId === presetFilters.assignedUserId,
        )[0]
      : null;

  const sourceImports =
    presetFilters.sourceImportTypeIds &&
    presetFilters.sourceImportTypeIds.length > 0
      ? SOURCE_IMPORT_TYPES_OPTIONS.filter((type) =>
          presetFilters.sourceImportTypeIds?.includes(Number(type.value)),
        )
      : null;

  const itemState =
    presetFilters.routeItemStateTypesId &&
    presetFilters.routeItemStateTypesId.length > 0
      ? ORDERS_GLOBALS_OPTIONS_WITHOUT_COLLECT.filter((state) =>
          presetFilters.routeItemStateTypesId?.includes(Number(state.value)),
        )
      : null;

  const giverCompany =
    presetFilters.giverCompanyId !== null &&
    presetFilters.giverCompanyId !== undefined
      ? selectedData.giverCompanies.filter(
          (currenGiverCompany) =>
            currenGiverCompany.id === presetFilters.giverCompanyId,
        )[0]
      : null;

  const driver =
    presetFilters.driverId !== null && presetFilters.driverId !== undefined
      ? selectedData.drivers.filter(
          (currentDriver) => currentDriver.id === presetFilters.driverId,
        )[0]
      : null;

  const collectState =
    presetFilters.collectItemStateTypesId &&
    presetFilters.collectItemStateTypesId.length > 0
      ? COLLECT_STATE_OPTIONS.filter((state) =>
          presetFilters.collectItemStateTypesId?.includes(Number(state.value)),
        )
      : null;

  if (!accessFeature.limitNoPro) {
    unit1Gt = '';
    unit1Lt = '';
    unit2Gt = '';
    unit2Lt = '';
    services = null;
    assignedUser = null;
  }

  if (!accessFeature.mgmZones) {
    zones = null;
  }

  return {
    // COMMONS
    search,
    expirationDateTimeFrom,
    expirationDateTimeTo,
    includeWithoutExpirationDate,
    dateFrom,
    dateTo,
    // SPECIFIC ITEMS
    finalizedDateTimeFrom,
    finalizedDateTimeTo,
    unit1Gt,
    unit1Lt,
    unit2Gt,
    unit2Lt,
    priceGt,
    priceLt,
    vehicles,
    drivers,
    services,
    zones,
    assignedUser,
    sourceImports,
    itemState,
    // SPECIFIC COLLECT
    giverCompany,
    driver,
    collectState,
  };
};

export const convertOrderListFilterToItemFilter = (
  currentFilters: OrdersListFilters,
  accessFeature: {
    limitNoPro: boolean;
    mgmZones: boolean;
  },
): ItemFilter => {
  const dateFrom = currentFilters.dateFrom
    ? formatterDate(new Date(currentFilters.dateFrom), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const dateTo = currentFilters.dateTo
    ? formatterDate(new Date(currentFilters.dateTo), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const routeCode = currentFilters.search || null;

  const maxDeliveredDateTime = currentFilters.expirationDate
    ? formatterDate(new Date(currentFilters.expirationDate), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  let routeItemStateTypesId =
    currentFilters.state !== null ? [currentFilters.state] : [];

  const collectItemStateTypesId =
    currentFilters.itemState !== null ? [currentFilters.itemState] : [];

  if (collectItemStateTypesId && collectItemStateTypesId.length > 0) {
    routeItemStateTypesId = [RouteItemStateType.New];
  }

  const driversId =
    currentFilters.drivers && currentFilters.drivers.length > 0
      ? currentFilters.drivers.map((driver) => driver.id)
      : null;

  const vehiclesId =
    currentFilters.vehicles && currentFilters.vehicles.length > 0
      ? currentFilters.vehicles.map((vehicle) => vehicle.id)
      : null;

  let serviceTypeList =
    currentFilters.services && currentFilters.services.length > 0
      ? currentFilters.services.map((service) => service.value.toString())
      : null;

  let zonesId =
    currentFilters.zones && currentFilters.zones.length > 0
      ? currentFilters.zones.map((zone) => zone.zoneId)
      : null;

  let unit1GraterThan = currentFilters.unit1Gt
    ? Number(currentFilters.unit1Gt)
    : null;

  let unit1LowerThan = currentFilters.unit1Lt
    ? Number(currentFilters.unit1Lt)
    : null;

  let unit2GraterThan = currentFilters.unit2Gt
    ? Number(currentFilters.unit2Gt)
    : null;

  let unit2LowerThan = currentFilters.unit2Lt
    ? Number(currentFilters.unit2Lt)
    : null;

  const priceGraterThan = currentFilters.priceGt
    ? Number(currentFilters.priceGt)
    : null;

  const priceLowerThan = currentFilters.priceLt
    ? Number(currentFilters.priceLt)
    : null;

  let assignedUserId = currentFilters.assignedUser
    ? currentFilters.assignedUser.userId
    : null;

  if (!accessFeature.limitNoPro) {
    serviceTypeList = null;
    unit1GraterThan = null;
    unit1LowerThan = null;
    unit2GraterThan = null;
    unit2LowerThan = null;
    assignedUserId = null;
  }

  if (!accessFeature.mgmZones) {
    zonesId = null;
  }

  return {
    dateFrom,
    dateTo,
    routeCode,
    maxDeliveredDateTime,
    routeItemStateTypesId,
    collectItemStateTypesId,
    driversId,
    vehiclesId,
    serviceTypeList,
    zonesId,
    unit1GraterThan,
    unit1LowerThan,
    unit2GraterThan,
    unit2LowerThan,
    priceGraterThan,
    priceLowerThan,
    assignedUserId,
  };
};

export const convertItemSearchFiltersToItemOrCollectFilter = (
  filterTabId: string,
  currentFilters: ItemsSearchFilters,
  accessFeature: {
    limitNoPro: boolean;
    mgmZones: boolean;
  },
): { tabId: string } & ItemFilterV2Base & PartnerCompanyItemFilterV2Base => {
  const routeCode = currentFilters.search || null;

  const maxDeliveredDateTimeFrom = currentFilters.expirationDateTimeFrom
    ? formatterDate(new Date(currentFilters.expirationDateTimeFrom), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const maxDeliveredDateTimeTo = currentFilters.expirationDateTimeTo
    ? formatterDate(new Date(currentFilters.expirationDateTimeTo), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const includeWithoutMaxDeliveryDatetime =
    currentFilters.includeWithoutExpirationDate;

  const dateFrom = currentFilters.dateFrom
    ? formatterDate(new Date(currentFilters.dateFrom), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const dateTo = currentFilters.dateTo
    ? formatterDate(new Date(currentFilters.dateTo), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const finalizedDateTimeFrom = currentFilters.finalizedDateTimeFrom
    ? formatterDate(new Date(currentFilters.finalizedDateTimeFrom), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  const finalizedDateTimeTo = currentFilters.finalizedDateTimeTo
    ? formatterDate(new Date(currentFilters.finalizedDateTimeTo), {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      })
    : null;

  let unit1GraterThan = currentFilters.unit1Gt
    ? Number(currentFilters.unit1Gt)
    : null;

  let unit1LowerThan = currentFilters.unit1Lt
    ? Number(currentFilters.unit1Lt)
    : null;

  let unit2GraterThan = currentFilters.unit2Gt
    ? Number(currentFilters.unit2Gt)
    : null;

  let unit2LowerThan = currentFilters.unit2Lt
    ? Number(currentFilters.unit2Lt)
    : null;

  const priceGraterThan = currentFilters.priceGt
    ? Number(currentFilters.priceGt)
    : null;

  const priceLowerThan = currentFilters.priceLt
    ? Number(currentFilters.priceLt)
    : null;

  const vehiclesId =
    currentFilters.vehicles && currentFilters.vehicles.length > 0
      ? currentFilters.vehicles.map((vehicle) => vehicle.id)
      : null;

  const driversId =
    currentFilters.drivers && currentFilters.drivers.length > 0
      ? currentFilters.drivers.map((driver) => driver.id)
      : null;

  let serviceTypeList =
    currentFilters.services && currentFilters.services.length > 0
      ? currentFilters.services.map((service) => service.value.toString())
      : null;

  let zonesId =
    currentFilters.zones && currentFilters.zones.length > 0
      ? currentFilters.zones.map((zone) => zone.zoneId)
      : null;

  let assignedUserId = currentFilters.assignedUser
    ? currentFilters.assignedUser.userId
    : null;

  const sourceImportTypeIds =
    currentFilters.sourceImports && currentFilters.sourceImports.length > 0
      ? currentFilters.sourceImports.map((sourceImport) =>
          Number(sourceImport.value),
        )
      : null;

  const routeItemStateTypesId =
    currentFilters.itemState && currentFilters.itemState.length > 0
      ? currentFilters.itemState.map((state) => Number(state.value))
      : [];

  const giverCompanyId = currentFilters.giverCompany
    ? currentFilters.giverCompany.id
    : null;

  const driverId = currentFilters.driver ? currentFilters.driver.id : null;

  const collectItemStateTypesId =
    currentFilters.collectState && currentFilters.collectState.length > 0
      ? currentFilters.collectState.map((state) => Number(state.value))
      : [];

  if (!accessFeature.limitNoPro) {
    unit1GraterThan = null;
    unit1LowerThan = null;
    unit2GraterThan = null;
    unit2LowerThan = null;
    serviceTypeList = null;
    assignedUserId = null;
  }

  if (!accessFeature.mgmZones) {
    zonesId = null;
  }

  return {
    tabId: filterTabId,
    // COMMONS
    routeCode,
    maxDeliveredDateTimeFrom,
    maxDeliveredDateTimeTo,
    includeWithoutMaxDeliveryDatetime,
    dateFrom,
    dateTo,
    // SPECIFIC ITEMS
    finalizedDateTimeFrom,
    finalizedDateTimeTo,
    unit1GraterThan,
    unit1LowerThan,
    unit2GraterThan,
    unit2LowerThan,
    priceGraterThan,
    priceLowerThan,
    vehiclesId,
    driversId,
    serviceTypeList,
    zonesId,
    assignedUserId,
    sourceImportTypeIds,
    routeItemStateTypesId,
    // SPECIFIC COLLECT
    giverCompanyId,
    driverId,
    collectItemStateTypesId,
  };
};
